import React, { useEffect, useRef, useState } from "react";
import Sidebar from "../Reusables/Sidebar";
import Navbar from "../Reusables/Navbar";
import Pagination from "../Reusables/Pagination";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ReactComponent as Calendar } from "../../assets/calendar.svg";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import empty from "../../assets/empty.png";
import {
  createEkedcPayment,
  deleteEkedcPayment,
  ekedcPaymentKeys,
  fetchEkedcPayments,
  formatPaymentDate,
  updateEkedcPayment,
} from "../api/ekedcPayments";

const initialForm = {
  paymentDate: new Date(),
  amount: "",
  description: "",
  referenceNumber: "",
};

const EkedcPayment = ({ title }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dateRef = useRef(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [paymentToDelete, setPaymentToDelete] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [currentPage, setCurrentPage] = useState(0);
  const [activater, setActivater] = useState(1);
  const postsPerPage = 10;
  const isEditing = editingId != null;

  useEffect(() => {
    if (!sessionStorage.getItem("token")) {
      navigate("/");
      toast.error("You aren't logged in");
    }
  }, [navigate]);

  const paymentsQuery = useQuery({
    queryKey: ekedcPaymentKeys.list(currentPage, postsPerPage),
    queryFn: () =>
      fetchEkedcPayments({ page: currentPage, size: postsPerPage }),
    enabled: !!sessionStorage.getItem("token"),
  });

  const saveMutation = useMutation({
    mutationFn: (payload) =>
      isEditing
        ? updateEkedcPayment({ id: editingId, ...payload })
        : createEkedcPayment(payload),
    onSuccess: (data) => {
      if (!data?.status) {
        toast.error(
          data?.message ||
            (isEditing ? "Failed to update payment" : "Failed to save payment")
        );
        return;
      }
      toast.success(
        data?.message ||
          (isEditing
            ? "Payment updated successfully."
            : "Payment saved successfully.")
      );
      setModalOpen(false);
      setEditingId(null);
      setForm(initialForm);
      queryClient.invalidateQueries({ queryKey: ekedcPaymentKeys.all });
    },
    onError: (error) => {
      toast.error(
        error?.message ||
          (isEditing ? "Failed to update payment" : "Failed to save payment")
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteEkedcPayment,
    onSuccess: (data) => {
      if (data && data.status === false) {
        toast.error(data?.message || "Failed to delete payment");
        return;
      }
      toast.success(data?.message || "Payment deleted successfully.");
      setPaymentToDelete(null);
      queryClient.invalidateQueries({ queryKey: ekedcPaymentKeys.all });
    },
    onError: (error) => {
      toast.error(error?.message || "Failed to delete payment");
    },
  });

  const openDeleteModal = (row) => {
    setPaymentToDelete(row);
  };

  const closeDeleteModal = () => {
    if (deleteMutation.isPending) return;
    setPaymentToDelete(null);
  };

  const confirmDelete = () => {
    if (!paymentToDelete?.id) return;
    deleteMutation.mutate(paymentToDelete.id);
  };

  const payments = paymentsQuery.data?.data?.content || [];
  const totalPosts = paymentsQuery.data?.data?.totalElements || 0;

  const paginate = (number) => {
    setCurrentPage(number - 1);
    setActivater(number);
  };

  const formatAmount = (value) => {
    if (value == null || value === "") return "—";
    return Number(value).toLocaleString();
  };

  const openCreateModal = () => {
    setEditingId(null);
    setForm({ ...initialForm, paymentDate: new Date() });
    setModalOpen(true);
  };

  const openEditModal = (row) => {
    setEditingId(row.id);
    setForm({
      paymentDate: row.paymentDate ? new Date(row.paymentDate) : new Date(),
      amount: row.amount ?? "",
      description: row.description ?? "",
      referenceNumber: row.referenceNumber ?? "",
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saveMutation.isPending) return;
    setModalOpen(false);
    setEditingId(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.paymentDate) {
      toast.error("Payment date is required");
      return;
    }
    if (!form.amount || Number(form.amount) <= 0) {
      toast.error("Enter a valid amount paid to EKEDC");
      return;
    }
    if (!form.description?.trim()) {
      toast.error("Description / narration is required");
      return;
    }
    if (!form.referenceNumber?.trim()) {
      toast.error("Reference number is required");
      return;
    }

    saveMutation.mutate({
      paymentDate: formatPaymentDate(form.paymentDate),
      amount: Number(form.amount),
      description: form.description.trim(),
      referenceNumber: form.referenceNumber.trim(),
    });
  };

  return (
    <div className="flex flex-row h-screen overflow-hidden">
      <div className="w-[15%] shrink-0 h-screen">
        <Sidebar />
      </div>
      <div className="flex flex-col w-[85%] h-screen overflow-y-auto">
        <div className="w-[100%] shrink-0">
          <Navbar title={title} />
        </div>
        <div className="w-[100%] pt-2 pb-9 px-5 flex flex-col gap-6">
          <div className="flex flex-row justify-between items-center gap-3 flex-wrap">
            <span className="text-route-name text-[24px] font-semibold">
              EKEDC Payments
            </span>
            <button
              type="button"
              onClick={openCreateModal}
              className="px-4 h-[38px] bg-route-color rounded-custom text-white font-semibold text-[12px]"
            >
              Record Payment
            </button>
          </div>

          <div className="flex flex-col border border-input-color rounded-custom py-4 gap-4 bg-white">
            {paymentsQuery.isPending || paymentsQuery.isFetching ? (
              <div className="flex justify-center items-center py-16">
                <div
                  className="w-10 h-10 rounded-full animate-spin"
                  style={{
                    background:
                      "radial-gradient(farthest-side, #999999 94%, #0000) top/6px 6px no-repeat, conic-gradient(#0000 30%, #999999)",
                    WebkitMask:
                      "radial-gradient(farthest-side, #0000 calc(100% - 6px), #000 0)",
                    mask: "radial-gradient(farthest-side, #0000 calc(100% - 6px), #000 0)",
                  }}
                  aria-label="Loading payments"
                />
              </div>
            ) : payments.length > 0 ? (
              <>
                <div className="overflow-x-auto px-3">
                  <table className="w-full text-left text-[12px]">
                    <thead>
                      <tr className="border-b text-route-noncolor">
                        <th className="py-3 px-2 font-semibold">S/N</th>
                        <th className="py-3 px-2 font-semibold">
                          Payment Date
                        </th>
                        <th className="py-3 px-2 font-semibold">
                          Amount Paid
                        </th>
                        <th className="py-3 px-2 font-semibold">
                          Description / Narration
                        </th>
                        <th className="py-3 px-2 font-semibold">
                          Reference Number
                        </th>
                        <th className="py-3 px-2 font-semibold">Created At</th>
                        <th className="py-3 px-2 font-semibold">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((row, index) => (
                        <tr
                          key={row.id}
                          className="border-b last:border-b-0 text-black"
                        >
                          <td className="py-3 px-2">
                            {currentPage * postsPerPage + index + 1}
                          </td>
                          <td className="py-3 px-2">{row.paymentDate}</td>
                          <td className="py-3 px-2 font-medium">
                            ₦{formatAmount(row.amount)}
                          </td>
                          <td className="py-3 px-2 max-w-[260px]">
                            {row.description}
                          </td>
                          <td className="py-3 px-2">{row.referenceNumber}</td>
                          <td className="py-3 px-2">
                            {row.createdAt
                              ? new Date(row.createdAt).toLocaleString()
                              : "—"}
                          </td>
                          <td className="py-3 px-2">
                            <div className="flex flex-row gap-2">
                              <button
                                type="button"
                                onClick={() => openEditModal(row)}
                                className="px-3 h-[30px] border border-route-color text-route-color rounded-custom font-semibold text-[11px] hover:bg-route-bg"
                              >
                                Update
                              </button>
                              <button
                                type="button"
                                onClick={() => openDeleteModal(row)}
                                className="px-3 h-[30px] border border-red-500 text-red-500 rounded-custom font-semibold text-[11px] hover:bg-red-50"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {totalPosts > postsPerPage && (
                  <Pagination
                    set={activater}
                    currentPage={currentPage}
                    postsPerPage={postsPerPage}
                    totalPosts={totalPosts}
                    paginate={paginate}
                  />
                )}
              </>
            ) : (
              <div className="flex flex-col justify-center items-center py-10">
                <img src={empty} alt="No payments yet" />
                <p className="text-[13px] text-route-noncolor mt-2">
                  No EKEDC payments recorded yet
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {modalOpen && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 px-4"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-custom w-full max-w-md p-6 flex flex-col gap-4 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-row justify-between items-center">
              <h2 className="text-[16px] font-semibold text-route-name">
                {isEditing ? "Update EKEDC Payment" : "Record EKEDC Payment"}
              </h2>
              <button
                type="button"
                onClick={closeModal}
                className="text-[20px] leading-none text-route-noncolor"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
              <label className="flex flex-col gap-1 text-[12px]">
                <span className="font-medium">Payment Date</span>
                <div className="relative h-[38px] rounded-custom px-2 flex flex-row border items-center">
                  <DatePicker
                    className="text-[12px] outline-none w-full"
                    selected={form.paymentDate}
                    onChange={(date) =>
                      setForm((prev) => ({ ...prev, paymentDate: date }))
                    }
                    ref={dateRef}
                    dateFormat="yyyy-MM-dd"
                    placeholderText="Select payment date"
                    popperPlacement="bottom-start"
                  />
                  <Calendar
                    className="text-[10px] cursor-pointer shrink-0"
                    onClick={() => dateRef.current?.setOpen(true)}
                  />
                </div>
              </label>

              <label className="flex flex-col gap-1 text-[12px]">
                <span className="font-medium">Amount Paid to EKEDC</span>
                <input
                  type="number"
                  min="1"
                  step="any"
                  value={form.amount}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, amount: e.target.value }))
                  }
                  className="h-[38px] border rounded-custom px-3 outline-none text-[12px]"
                  placeholder="e.g. 2000000"
                  required
                />
              </label>

              <label className="flex flex-col gap-1 text-[12px]">
                <span className="font-medium">Description / Narration</span>
                <textarea
                  value={form.description}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  className="min-h-[80px] border rounded-custom px-3 py-2 outline-none text-[12px] resize-y"
                  placeholder="Enter narration"
                  required
                />
              </label>

              <label className="flex flex-col gap-1 text-[12px]">
                <span className="font-medium">Reference Number</span>
                <input
                  type="text"
                  value={form.referenceNumber}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      referenceNumber: e.target.value,
                    }))
                  }
                  className="h-[38px] border rounded-custom px-3 outline-none text-[12px]"
                  placeholder="e.g. 212FG3523"
                  required
                />
              </label>

              <div className="flex flex-row justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 h-[36px] border rounded-custom text-[12px] font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saveMutation.isPending}
                  className="px-4 h-[36px] bg-route-color rounded-custom text-white text-[12px] font-semibold disabled:opacity-60"
                >
                  {saveMutation.isPending
                    ? isEditing
                      ? "Updating..."
                      : "Saving..."
                    : isEditing
                      ? "Update Payment"
                      : "Save Payment"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {paymentToDelete && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 px-4"
          onClick={closeDeleteModal}
        >
          <div
            className="bg-white rounded-custom w-full max-w-sm p-6 flex flex-col gap-4 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-row justify-between items-center">
              <h2 className="text-[16px] font-semibold text-route-name">
                Delete Payment
              </h2>
              <button
                type="button"
                onClick={closeDeleteModal}
                className="text-[20px] leading-none text-route-noncolor"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <p className="text-[13px] text-route-noncolor leading-relaxed">
              Are you sure you want to delete payment{" "}
              <span className="font-semibold text-black">
                {paymentToDelete.referenceNumber || paymentToDelete.id}
              </span>
              ? This action cannot be undone.
            </p>
            <div className="flex flex-row justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={closeDeleteModal}
                disabled={deleteMutation.isPending}
                className="px-4 h-[36px] border rounded-custom text-[12px] font-semibold disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={deleteMutation.isPending}
                className="px-4 h-[36px] bg-red-500 rounded-custom text-white text-[12px] font-semibold disabled:opacity-60"
              >
                {deleteMutation.isPending ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EkedcPayment;
